/**
 * PassengerHome
 * -------------
 * Post-login landing page for passengers only. Displays a large hero image
 * with 4 clickable cards (Trigo Express, Trigo Location, Mes Réservations,
 * Paramètres). The image is stored locally at `/passenger-home.jpg` and
 * we overlay 4 accessible <button> hotspots on top so each card zone
 * navigates to the right route without relying on image dimensions.
 *
 * The hotspots are positioned in percent so the layout stays correct
 * regardless of the viewport size. On very small screens (< 380px width)
 * the image is scaled to fit the width while preserving aspect ratio.
 *
 * The driver dashboard is unaffected — this page is only mounted for
 * passengers via the `home` tab in PassengerDashboard.
 */
import { useNavigate } from "react-router-dom";
import { Zap, Car, CalendarCheck, Settings } from "lucide-react";

// 4 hotspot zones expressed as percentage of the image dimensions.
// Values match the exact card positions in `/public/passenger-home.jpg`.
// Top row is 0%, bottom row is 100%.
const HOTSPOTS = [
  {
    id: "express",
    label: "Trigo Express — Course VTC rapide",
    top: 20.5, left: 2.5, width: 95, height: 17.5,
    to: "/express",
    testid: "home-card-express",
  },
  {
    id: "location",
    label: "Trigo Location — Location de véhicule",
    top: 39.5, left: 2.5, width: 95, height: 17.5,
    to: "/passenger?tab=vehicles",
    testid: "home-card-location",
  },
  {
    id: "reservations",
    label: "Mes Réservations",
    top: 58.5, left: 2.5, width: 95, height: 17.5,
    to: "/passenger?tab=bookings",
    testid: "home-card-reservations",
  },
  {
    id: "settings",
    label: "Paramètres",
    top: 77.5, left: 2.5, width: 95, height: 17,
    to: "/passenger?tab=profile",
    testid: "home-card-settings",
  },
];

export default function PassengerHome() {
  const navigate = useNavigate();

  const go = (to) => {
    // Same-domain in-app navigation. `/express` is a full route change;
    // the tab-based URLs stay inside PassengerDashboard so the shell
    // (header + navbar) is not rerendered.
    navigate(to);
  };

  return (
    <div
      className="w-full min-h-[calc(100vh-120px)] flex items-start justify-center bg-[#0A2540] py-4 sm:py-6"
      data-testid="passenger-home"
    >
      <div className="relative w-full max-w-[520px] mx-auto px-3 sm:px-0">
        {/* The image is the single visual source of truth. It's shown at
            100% width and preserves aspect ratio thanks to `w-full h-auto`.
            WebP is preferred (≈60 KB) with a JPEG fallback (≈100 KB) —
            both are pre-compressed to load fast even on 3G connections. */}
        <picture>
          <source srcSet="/passenger-home.webp" type="image/webp" />
          <img
            src="/passenger-home.jpg"
            alt="Bienvenue sur Trigo Tour — Choisissez votre service"
            className="w-full h-auto rounded-2xl shadow-2xl select-none block"
            draggable={false}
            fetchpriority="high"
            decoding="async"
            width="682"
            height="1000"
            data-testid="home-hero-image"
          />
        </picture>

        {/* Absolute-positioned invisible buttons that turn each card in the
            image into a real accessible link. Kept invisible so the artwork
            drives the look; a subtle hover overlay gives feedback. */}
        {HOTSPOTS.map((h) => (
          <button
            key={h.id}
            type="button"
            onClick={() => go(h.to)}
            aria-label={h.label}
            data-testid={h.testid}
            className="group absolute rounded-2xl transition-all duration-200
                       hover:bg-white/10 hover:shadow-[0_0_0_2px_rgba(250,204,21,0.5)]
                       focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FACC15]
                       active:scale-[0.985]"
            style={{
              top: `${h.top}%`,
              left: `${h.left}%`,
              width: `${h.width}%`,
              height: `${h.height}%`,
            }}
          />
        ))}
      </div>

      {/* Accessible text fallback (screen readers) — keeps the page usable
          even if the image fails to load. Also renders as textual quick
          links for keyboard users. Visually hidden on desktop/mobile but
          still focusable. */}
      <div className="sr-only" aria-label="Menu principal passager">
        <ul>
          <li><a href="/express">Trigo Express</a></li>
          <li><a href="/passenger?tab=vehicles">Trigo Location</a></li>
          <li><a href="/passenger?tab=bookings">Mes Réservations</a></li>
          <li><a href="/passenger?tab=profile">Paramètres</a></li>
        </ul>
      </div>

      {/* Text-first fallback that appears ONLY if the image failed to
          decode (very low bandwidth). Rendered below via error handler. */}
      <FallbackGrid onGo={go} />
    </div>
  );
}

// Text/icon-based fallback card grid rendered only when the hero image
// fails to load. Guarantees the page stays usable in every situation.
function FallbackGrid({ onGo }) {
  const items = [
    { id: "express", icon: Zap, title: "Trigo Express", desc: "Course VTC rapide d'un point à un autre.", color: "bg-[#FACC15] text-[#0A2540]", to: "/express" },
    { id: "location", icon: Car, title: "Trigo Location", desc: "Location de véhicule en ville et hors ville.", color: "bg-[#3B82F6] text-white", to: "/passenger?tab=vehicles" },
    { id: "reservations", icon: CalendarCheck, title: "Mes Réservations", desc: "Vos réservations présentes, futures et passées.", color: "bg-[#10B981] text-white", to: "/passenger?tab=bookings" },
    { id: "settings", icon: Settings, title: "Paramètres", desc: "Profil et paramètres du compte.", color: "bg-[#7C3AED] text-white", to: "/passenger?tab=profile" },
  ];
  return (
    <noscript>
      <div className="w-full max-w-md mx-auto grid grid-cols-1 gap-3 mt-4">
        {items.map((it) => (
          <a key={it.id} href={it.to} className={`block rounded-2xl p-4 ${it.color}`}>
            <div className="flex items-center gap-3">
              <it.icon className="w-6 h-6" />
              <div>
                <p className="font-extrabold" style={{ fontFamily: "Outfit" }}>{it.title}</p>
                <p className="text-xs opacity-90">{it.desc}</p>
              </div>
            </div>
          </a>
        ))}
      </div>
    </noscript>
  );
}
